/**
 * AI 聊天外观：浅色 / 深色 / 跟随系统（localStorage 持久化）
 */

import { create } from 'zustand';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'sse_ai_chat_theme_pref';

function loadPref(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

/**
 * 是否与当前页面「主题夜间模式」一致（不用取背景色猜）：
 * - 与本插件 assets/css/style.css 一致：body.dark-theme / 子比 Zibll 等
 * - WordPress 后台用户配色「午夜」：body.admin-color-midnight
 * - 常见 html/body 标记：.dark、.dark-mode、data-theme="dark"、data-color-mode="dark"
 */
function isHostPageDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const html = document.documentElement;
    const body = document.body;
    if (!body) return false;

    if (body.classList.contains('dark-theme') || html.classList.contains('dark-theme')) {
      return true;
    }
    if (body.classList.contains('dark') || html.classList.contains('dark')) {
      return true;
    }
    if (body.classList.contains('dark-mode') || html.classList.contains('dark-mode')) {
      return true;
    }
    if (body.classList.contains('admin-color-midnight')) {
      return true;
    }
    const dt = html.getAttribute('data-theme') || body.getAttribute('data-theme');
    if (dt === 'dark') return true;
    const cm = html.getAttribute('data-color-mode') || body.getAttribute('data-color-mode');
    if (cm === 'dark') return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * 跟随「自动」：先页面主题/后台配色标记，再 prefers-color-scheme（与 style.css 中夜间逻辑顺序一致）
 */
function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  try {
    if (isHostPageDarkMode()) {
      return 'dark';
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch {
    /* matchMedia 不可用 */
  }
  return 'dark';
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'light') return 'light';
  if (pref === 'dark') return 'dark';
  return resolveSystemTheme();
}

/**
 * 每次点击打开聊天时调用：在用户手势后重新读 localStorage + matchMedia，
 * 避免脚本过早执行时 prefers-color-scheme 与后台环境未就绪导致状态错误。
 */
export function hydrateThemeAtChatOpen(): void {
  const pref = loadPref();
  useThemeStore.setState({
    themePreference: pref,
    resolvedTheme: resolveTheme(pref),
  });
}

const initialPref = typeof window !== 'undefined' ? loadPref() : 'system';
const initialResolved =
  typeof window !== 'undefined' ? resolveTheme(initialPref) : 'dark';

interface ThemeState {
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setThemePreference: (p: ThemePreference) => void;
  /** 在 preference 为 system 时根据当前系统配色刷新 */
  syncResolved: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  themePreference: initialPref,
  resolvedTheme: initialResolved,

  setThemePreference: (p) => {
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* ignore */
    }
    set({ themePreference: p, resolvedTheme: resolveTheme(p) });
  },

  syncResolved: () => {
    set({ resolvedTheme: resolveTheme(get().themePreference) });
  },
}));

let systemListenerAttached = false;

/** 在应用挂载时调用一次：系统深浅色变化时更新 resolvedTheme */
export function attachSystemThemeListener(): void {
  if (systemListenerAttached || typeof window === 'undefined') return;
  systemListenerAttached = true;

  const onSchemeChange = () => {
    if (useThemeStore.getState().themePreference === 'system') {
      useThemeStore.getState().syncResolved();
    }
  };

  const subscribe = (query: string) => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(query);
    } catch {
      return;
    }
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onSchemeChange);
    } else {
      mq.addListener(onSchemeChange);
    }
  };

  subscribe('(prefers-color-scheme: light)');
  subscribe('(prefers-color-scheme: dark)');

  /* 子比 dark-theme、WP 午夜配色等只改 class/data，不触发 prefers-color-scheme */
  let shellDebounce: ReturnType<typeof setTimeout> | null = null;
  const onShellMaybeChanged = () => {
    if (useThemeStore.getState().themePreference !== 'system') return;
    if (shellDebounce) clearTimeout(shellDebounce);
    shellDebounce = setTimeout(() => {
      shellDebounce = null;
      useThemeStore.getState().syncResolved();
    }, 150);
  };

  try {
    const mo = new MutationObserver(onShellMaybeChanged);
    if (document.documentElement) {
      mo.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'data-color-mode'],
      });
    }
    if (document.body) {
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  } catch {
    /* MutationObserver 不可用 */
  }
}
